import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";

import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";

import Home from "./pages/Home";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Reviews from "./pages/Reviews";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Register from "./pages/Register";
import Checkout from "./pages/Checkout";
import PendingApproval from "./pages/PendingApproval";
import PayInvoice from "./pages/PayInvoice";
import NotFound from "./pages/NotFound";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/**
 * Links that have to work on static hosting are minted by the API in hash form
 * (`<marketing-url>/#/pay/<token>`) so they survive shared hosting with no
 * history fallback. Such a host answers a deep link like /reset-password with
 * its own 404, so a password reset would die before the site even loaded,
 * while the hash always reaches index.html. This site uses BrowserRouter, so
 * translate those hash shapes back into real routes, query string included.
 *
 * Only the paths listed are translated, so ordinary anchors such as `#features` keep working and a
 * stray hash cannot push a visitor somewhere unexpected.
 */
const HASH_ROUTES =
  /^#(\/(?:pay\/|reset-password|forgot-password|accept-invite|login|register)[^#]*)$/;

function HashRouteRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const match = window.location.hash.match(HASH_ROUTES);
    if (match) navigate(match[1], { replace: true });
  }, [navigate]);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <HashRouteRedirect />
        <Routes>
          {/* Standalone: the customer paying an invoice is not a site visitor. */}
          <Route path="/pay/:token" element={<PayInvoice />} />
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/pending-approval" element={<PendingApproval />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
